import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { PROJECTS, GRID_SPANS, posterOf } from "@/data/studio";
import { Eyebrow, H2, Pill, Reveal, SEASE } from "./ui";

const Card = ({ p, span, onOpen }) => {
  const ref = useRef(null);
  const near = useInView(ref, { margin: "400px", once: true });
  return (
    <motion.button ref={ref} type="button" onClick={() => onOpen(p)} data-cursor="view" data-testid={`studio-project-${p.id}`}
      initial={{ opacity: 0, y: 70 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 1, ease: SEASE }}
      className={`group relative block w-full overflow-hidden rounded bg-coal text-left ${span}`}>
      <img src={posterOf(p.src)} alt={`${p.title} — ${p.category.toLowerCase()}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]" />
      {near && <video src={p.src} poster={posterOf(p.src)} autoPlay muted loop playsInline preload="metadata"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]" />}
      <div className="absolute inset-0 bg-gradient-to-t from-sink/90 via-sink/10 to-transparent" />
      <span className="absolute left-4 top-4 rounded-full border border-white/20 bg-sink/50 px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] text-white backdrop-blur-md sm:left-6 sm:top-6">{p.category}</span>
      <span className="absolute right-4 top-4 flex h-11 w-11 scale-75 items-center justify-center rounded-full bg-brand text-white opacity-0 transition-[opacity,transform] duration-500 group-hover:scale-100 group-hover:opacity-100 sm:right-6 sm:top-6">
        <ArrowUpRight className="h-5 w-5" />
      </span>
      <div className="absolute inset-x-4 bottom-4 flex items-end gap-4 sm:inset-x-6 sm:bottom-6">
        <span className="font-mono text-[11px] text-brand">{String(p.id).padStart(2, "0")}</span>
        <span className="font-display text-2xl font-bold leading-none text-white sm:text-3xl">{p.title}</span>
      </div>
    </motion.button>
  );
};

const VideoModal = ({ p, onClose }) => {
  useEffect(() => {
    const l = window.__lenis;
    l?.stop();
    const key = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", key);
    return () => { l?.start(); window.removeEventListener("keydown", key); };
  }, [onClose]);
  return (
    <motion.div data-testid="studio-video-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex flex-col bg-sink/95 backdrop-blur-xl" onClick={onClose}>
      <div className="flex items-center justify-between px-5 py-5 sm:px-8 lg:px-12">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] text-brand">{p.category}</p>
          <p data-testid="studio-modal-title" className="mt-1 font-display text-2xl font-bold text-white sm:text-3xl">{p.title}</p>
        </div>
        <button type="button" onClick={onClose} data-testid="studio-modal-close" aria-label="Close video"
          className="flex h-12 w-12 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-300 hover:border-brand hover:text-brand">
          <X className="h-5 w-5" />
        </button>
      </div>
      <motion.div initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6, ease: SEASE }}
        className="flex flex-1 items-center justify-center px-4 pb-8 sm:px-8" onClick={(e) => e.stopPropagation()}>
        <video src={p.src} poster={posterOf(p.src)} autoPlay controls playsInline data-testid="studio-modal-video" className="max-h-[80vh] w-full max-w-6xl rounded bg-black object-contain" />
      </motion.div>
    </motion.div>
  );
};

export const Portfolio = () => {
  const [active, setActive] = useState(null);
  return (
    <section id="work" data-testid="studio-work" className="bg-sink py-24 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-[1480px] px-5 sm:px-8 lg:px-12">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Reveal><Eyebrow>Selected Work</Eyebrow></Reveal>
            <Reveal delay={0.05}><H2 className="mt-6">See What <span className="text-brand">We Create.</span></H2></Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-4 lg:col-start-9">
            <p className="text-base text-zinc-400 sm:text-lg">AI can generate footage.</p>
            <p className="mt-2 text-lg font-semibold text-white sm:text-xl">We turn it into finished production.</p>
          </Reveal>
        </div>
        <div className="mt-16 grid grid-cols-1 gap-5 sm:mt-20 lg:grid-cols-12 lg:gap-8" data-testid="studio-work-grid">
          {PROJECTS.map((p, i) => <Card key={p.id} p={p} span={GRID_SPANS[i]} onOpen={setActive} />)}
        </div>
        <Reveal className="mt-20 flex justify-center"><Pill label="Start Your Project" testid="studio-work-start" /></Reveal>
      </div>
      <AnimatePresence>{active && <VideoModal key={active.id} p={active} onClose={() => setActive(null)} />}</AnimatePresence>
    </section>
  );
};
